// A ÁREA DE CADA LINHA do quadro de itens da proposta/venda (05/10/2026).
//
// Pedido do Lucas: "item livre tem que ter área". Toda linha com valor precisa
// de uma das 6 áreas da empresa — e da sub-área, quando a área tem —, porque é
// dos itens da venda que sai a divisão por área do contrato.
//
// Esta é a régua da TELA. A do servidor é
// `dre-sistema/lambda/comercial-engine/handlers/areaDoItem.js`, e
// `areaDoItem.test.ts` roda as duas lado a lado: a tela não pode liberar o
// "Salvar" para um quadro que o servidor recusa, nem o contrário.
import { SETORES, AREAS_EMPRESA, rotuloDaArea, type Subarea } from '../../utils/areasEmpresa';
import { CNAE_PADRAO_DA_AREA, cnaeValido } from '../../utils/cnaes';

export type ItemVenda = {
  servico_id?: string | null;
  descricao?: string | null;
  unidade?: string | null;
  quantidade?: number | string | null;
  valor_unitario?: number | string | null;
  desconto_pct?: number | string | null;
  setor_chave?: string | null;
  subarea_id?: string | null;
  cnae?: string | null;
  [campo: string]: any;
};
export type ServicoDoCatalogo = {
  id: string; nome?: string | null; codigo?: string | null; descricao?: string | null;
  unidade?: string | null; area?: string | null;
  setor_chave?: string | null; subarea_id?: string | null; cnae?: string | null;
};
export type Falta = 'area' | 'subarea' | 'subarea_invalida';
export type AreaEfetiva = {
  setor: string | null; subarea_id: string | null; cnae: string | null;
  origem: 'item' | 'servico' | null; segue_servico: boolean;
};
export type MapaServicos = Map<string, ServicoDoCatalogo>;

const txt = (v: unknown) => String(v ?? '').trim();
const numero = (v: unknown) => parseFloat(String(v ?? ''));
// `hasOwnProperty`: "constructor" e "toString" existem em todo objeto e não são área.
const ehArea = (chave: unknown): chave is string =>
  typeof chave === 'string' && Object.prototype.hasOwnProperty.call(SETORES, chave);
const cnaeOuNull = (c: unknown) => (cnaeValido(txt(c)) ? txt(c) : null);

export const mapaDeServicos = (servicos: ServicoDoCatalogo[]): MapaServicos =>
  new Map((servicos || []).map(s => [String(s.id), s]));
export const servicoDoItem = (item: ItemVenda | null | undefined, servicos: MapaServicos | null | undefined) =>
  (servicos && txt(item?.servico_id) ? servicos.get(txt(item?.servico_id)) || null : null);

// Quanto a linha vale: quantidade × valor unitário, com o desconto da linha.
// Quantidade ZERO é zero (a linha aparece com R$ 0,00); só a linha que não diz a
// quantidade conta como 1 — a mesma leitura do início do contrato.
export function valorDoItem(item: ItemVenda | null | undefined): number {
  const q0 = numero(item?.quantidade);
  const q = Number.isFinite(q0) ? q0 : 1;
  const v = numero(item?.valor_unitario) || 0;
  const d = numero(item?.desconto_pct);
  const fator = Number.isFinite(d) && d > 0 ? Math.max(0, 1 - d / 100) : 1;
  return q * v * fator;
}

// Só linha com valor precisa de área: "Desconto (25%)" com valor negativo e
// linha zerada não são receita de área nenhuma.
export const precisaDeArea = (item: ItemVenda | null | undefined) => valorDoItem(item) > 0;

// O que vale para a linha: a área escolhida nela → a do serviço do catálogo.
// A sub-área do serviço só vale se a linha está na área dele.
export function areaEfetivaDoItem(item: ItemVenda | null | undefined, servico?: ServicoDoCatalogo | null): AreaEfetiva {
  const it = item || {};
  const propria = ehArea(it.setor_chave) ? it.setor_chave : null;
  const doServico = servico && ehArea(servico.setor_chave) ? servico.setor_chave : null;
  const setor = propria || doServico || null;
  const segue = !!(doServico && doServico === setor);
  const subarea_id = txt(it.subarea_id) || (segue ? txt(servico?.subarea_id) : '') || null;
  const cnae = cnaeOuNull(it.cnae) || cnaeOuNull(servico?.cnae);
  return { setor, subarea_id, cnae, origem: propria ? 'item' : (doServico ? 'servico' : null), segue_servico: segue };
}

// O que falta na linha, ou null. Sem a lista de sub-áreas (`null`: não
// carregou) só a área é conferida.
export function faltaNoItem(
  item: ItemVenda | null | undefined, servico: ServicoDoCatalogo | null | undefined, subareas: Subarea[] | null,
): Falta | null {
  if (!precisaDeArea(item)) return null;
  const ef = areaEfetivaDoItem(item, servico);
  if (!ef.setor) return 'area';
  if (!Array.isArray(subareas)) return null;
  const propria = txt(item?.subarea_id);
  if (propria) {
    const s = subareas.find(x => String(x.id) === propria);
    return s && s.setor_chave === ef.setor ? null : 'subarea_invalida';
  }
  if (ef.subarea_id) return null;
  const exige = subareas.some(s => s.setor_chave === ef.setor && s.ativo);
  // Linha que só segue um serviço do catálogo sem sub-área: a pendência é do
  // catálogo (aba "Em aberto" de lá), não desta proposta.
  return exige && !ef.segue_servico ? 'subarea' : null;
}

export function nomeDoItem(item: ItemVenda | null | undefined, indice: number): string {
  const d = txt(item?.descricao).replace(/\s+/g, ' ');
  return d ? (d.length > 60 ? d.slice(0, 57) + '…' : d) : `Item ${indice + 1}`;
}

export function fraseDaFalta(falta: Falta, num: number, item: ItemVenda | null | undefined, setor: string | null): string {
  const quem = `Item ${num} (${nomeDoItem(item, num - 1)})`;
  const area = rotuloDaArea(setor) || 'a área';
  if (falta === 'area') return `${quem}: escolha a área.`;
  if (falta === 'subarea') return `${quem}: escolha a sub-área — ${area} tem sub-áreas.`;
  return `${quem}: a sub-área escolhida não é de ${area}. Escolha de novo.`;
}

export type ProblemaDoItem = { indice: number; numero: number; falta: Falta; texto: string };

// Tudo o que falta no quadro — o que trava o "Salvar".
export function problemasDosItens(
  itens: ItemVenda[] | null | undefined, servicos: MapaServicos | null, subareas: Subarea[] | null,
): ProblemaDoItem[] {
  const problemas: ProblemaDoItem[] = [];
  (Array.isArray(itens) ? itens : []).forEach((item, indice) => {
    const servico = servicoDoItem(item, servicos);
    const falta = faltaNoItem(item, servico, subareas);
    if (!falta) return;
    const setor = areaEfetivaDoItem(item, servico).setor;
    problemas.push({ indice, numero: indice + 1, falta, texto: fraseDaFalta(falta, indice + 1, item, setor) });
  });
  return problemas;
}

// Uma frase curta para o lado do botão Salvar: ONDE falta (as linhas). Quantas
// são já está escrito na faixa em cima do quadro — cada número uma vez.
export function resumoDosProblemas(problemas: ProblemaDoItem[]): string | null {
  if (!problemas.length) return null;
  const soArea = problemas.every(p => p.falta === 'area');
  const oQue = soArea ? 'a área' : 'a área ou a sub-área';
  if (problemas.length === 1) return `Falta ${oQue} na linha ${problemas[0].numero} — coluna Área do quadro de itens.`;
  const numeros = problemas.slice(0, 8).map(p => String(p.numero));
  const linhas = problemas.length > 8
    ? `${numeros.join(', ')} e outras`
    : `${numeros.slice(0, -1).join(', ')} e ${numeros[numeros.length - 1]}`;
  return `Falta ${oQue} nas linhas ${linhas} — coluna Área do quadro de itens.`;
}

// ─── O que acontece quando a pessoa mexe na linha ──────────────────────────

// O código da NF que a tela SUGERE para a linha que não tem um (item livre, ou
// serviço ainda sem código): o padrão da área dela. É só sugestão — não é
// gravado na linha. O padrão da área nunca vira código de contrato sem alguém
// conferir (batimetria lançada em Topografia não é o código padrão de Topografia):
// quem quer que o código valha escolhe na linha.
export function cnaeSugeridoDoItem(item: ItemVenda | null | undefined, servico?: ServicoDoCatalogo | null): string | null {
  const ef = areaEfetivaDoItem(item, servico);
  return ef.cnae || !ef.setor ? null : (CNAE_PADRAO_DA_AREA[ef.setor] || null);
}

// Escolheu um serviço do catálogo: a linha passa a seguir a área, a sub-área e
// o código dele. Serviço ainda sem área (ex.: ART) não apaga a que a linha tinha.
// Serviço sem código não deixa na linha o código do serviço anterior.
export function aoEscolherServico(item: ItemVenda, sp: ServicoDoCatalogo): ItemVenda {
  const setor = ehArea(sp.setor_chave) ? sp.setor_chave : null;
  return {
    ...item,
    servico_id: sp.id,
    descricao: (sp.nome || '') + (sp.descricao ? ` — ${sp.descricao}` : ''),
    unidade: sp.unidade || 'vb',
    setor_chave: setor || (ehArea(item.setor_chave) ? item.setor_chave : null),
    subarea_id: setor ? (txt(sp.subarea_id) || null) : (txt(item.subarea_id) || null),
    cnae: cnaeOuNull(sp.cnae),
  };
}

// Tirou o vínculo com o catálogo: a linha continua classificada como estava.
export function aoDesvincularServico(item: ItemVenda, servico?: ServicoDoCatalogo | null): ItemVenda {
  const ef = areaEfetivaDoItem(item, servico);
  return { ...item, servico_id: null, setor_chave: ef.setor, subarea_id: ef.subarea_id, cnae: ef.cnae };
}

// Trocou a área na linha. A sub-área é da área antiga e sai (volta a do serviço
// se a linha voltou para a área dele). O código: o do serviço continua valendo;
// o que a pessoa tinha escolhido à mão era para a área antiga e sai — a tela
// volta a sugerir o padrão da área nova, sem gravar (`cnaeSugeridoDoItem`).
export function aoTrocarAreaDoItem(item: ItemVenda, servico: ServicoDoCatalogo | null | undefined, nova: string | null): ItemVenda {
  const area = ehArea(nova) ? nova : null;
  const doServico = servico && ehArea(servico.setor_chave) ? servico.setor_chave : null;
  return {
    ...item,
    setor_chave: area,
    subarea_id: area && area === doServico ? (txt(servico?.subarea_id) || null) : null,
    cnae: cnaeOuNull(servico?.cnae),
  };
}

export function aoTrocarSubareaDoItem(item: ItemVenda, servico: ServicoDoCatalogo | null | undefined, subareaId: string | null): ItemVenda {
  const ef = areaEfetivaDoItem(item, servico);
  return { ...item, setor_chave: ef.setor, subarea_id: subareaId || null };
}

export function aoTrocarCnaeDoItem(item: ItemVenda, servico: ServicoDoCatalogo | null | undefined, cnae: string | null): ItemVenda {
  const ef = areaEfetivaDoItem(item, servico);
  return { ...item, setor_chave: ef.setor, subarea_id: ef.subarea_id, cnae: cnaeOuNull(cnae) };
}

// "Aplicar às linhas que faltam": a área (e a sub-área) escolhida vai para toda
// linha com valor e sem área; a sub-área vai também para as linhas que já estão
// naquela área e só não têm a sub-área. Nunca troca o que já foi escolhido.
export function aplicarEmLote(
  itens: ItemVenda[], servicos: MapaServicos | null, subareas: Subarea[] | null,
  escolha: { setor_chave: string | null; subarea_id: string | null },
): ItemVenda[] {
  if (!ehArea(escolha.setor_chave)) return itens;
  return itens.map(item => {
    const servico = servicoDoItem(item, servicos);
    const falta = faltaNoItem(item, servico, subareas);
    if (falta === 'area') {
      return { ...aoTrocarAreaDoItem(item, servico, escolha.setor_chave), subarea_id: escolha.subarea_id || null };
    }
    if (falta === 'subarea' && escolha.subarea_id && areaEfetivaDoItem(item, servico).setor === escolha.setor_chave) {
      return aoTrocarSubareaDoItem(item, servico, escolha.subarea_id);
    }
    return item;
  });
}

// Antes de salvar: escreve em cada linha a área, a sub-área e o código que
// estão valendo para ela (o que a tela mostra). A venda fica com o retrato do
// que foi vendido, mesmo que o catálogo mude depois.
export function prepararParaSalvar(itens: ItemVenda[], servicos: MapaServicos | null): ItemVenda[] {
  return (Array.isArray(itens) ? itens : []).map(item => {
    const ef = areaEfetivaDoItem(item, servicoDoItem(item, servicos));
    if (!ef.setor) return item;
    return { ...item, setor_chave: ef.setor, subarea_id: ef.subarea_id, cnae: ef.cnae };
  });
}

// Quanto do quadro é de cada área (o que vira a divisão do contrato). Linha sem
// valor (desconto, linha zerada) não entra: ela reduz todas na proporção. A
// proporção é sobre TODAS as linhas com valor — com tudo classificado, é
// exatamente a divisão que o contrato recebe; enquanto faltar área em alguma,
// a parte dela aparece em `semArea`, e não inflando as outras.
export function divisaoPorArea(itens: ItemVenda[] | null | undefined, servicos: MapaServicos | null) {
  const soma: Record<string, number> = {};
  let total = 0, semValor = 0;
  for (const item of (Array.isArray(itens) ? itens : [])) {
    const v = valorDoItem(item);
    if (!(v > 0)) continue;
    total += v;
    const setor = areaEfetivaDoItem(item, servicoDoItem(item, servicos)).setor;
    if (!setor) { semValor += v; continue; }
    soma[setor] = (soma[setor] || 0) + v;
  }
  const partes = AREAS_EMPRESA.filter(a => soma[a.chave] > 0).map(a => ({
    chave: a.chave, rotulo: a.rotulo, cor: a.cor, proporcao: total > 0 ? soma[a.chave] / total : 0,
  }));
  return { partes, semArea: total > 0 ? semValor / total : 0 };
}
